<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Integration from './Integration.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const OAUTH_BASE_URL = 'https://auth.akiles.app/oauth2/auth';
const SESSION_KEY = 'akiles_oauth_credentials';

const props = defineProps({
  code: { type: String, default: '' },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const integrationLoaded = ref(false);
const dialogRef = ref(null);
const clientId = ref('');
const clientSecret = ref('');
const isConnecting = ref(false);
const errorMessage = ref('');

const whatsappInboxId = ref('');
const bookingTemplateName = ref('');
const checkinTemplateName = ref('');

const integration = computed(() => {
  return store.getters['integrations/getIntegration']('akiles');
});

const isConnected = computed(() => {
  const { hooks = [] } = integration.value || {};
  return hooks.length > 0;
});

const uiFlags = computed(() => store.getters['integrations/getUIFlags']);

const currentHookSettings = computed(() => {
  const { hooks = [] } = integration.value || {};
  return hooks[0]?.settings || {};
});

const redirectUri = computed(
  () => `${window.location.origin}/app/auth/akiles/callback`
);

const openConnectForm = () => {
  errorMessage.value = '';
  if (dialogRef.value) dialogRef.value.open();
};

const loadSettingsFromHook = () => {
  const s = currentHookSettings.value;
  whatsappInboxId.value = s.whatsapp_inbox_id || '';
  bookingTemplateName.value = s.booking_template_name || '';
  checkinTemplateName.value = s.checkin_template_name || '';
};

const saveSettings = async () => {
  try {
    await store.dispatch('integrations/updateAkilesSettings', {
      whatsappInboxId: whatsappInboxId.value,
      bookingTemplateName: bookingTemplateName.value,
      checkinTemplateName: checkinTemplateName.value,
    });
    useAlert(t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.SAVE_SUCCESS'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.SAVE_ERROR'));
  }
};

const cancelConnect = () => {
  clientId.value = '';
  clientSecret.value = '';
  errorMessage.value = '';
  if (dialogRef.value) dialogRef.value.close();
};

const startOAuthFlow = () => {
  if (!clientId.value || !clientSecret.value) {
    errorMessage.value = t('INTEGRATION_SETTINGS.AKILES.ERRORS.CREDENTIALS_REQUIRED');
    return;
  }

  sessionStorage.setItem(
    SESSION_KEY,
    JSON.stringify({ clientId: clientId.value, clientSecret: clientSecret.value })
  );

  const state = btoa(
    JSON.stringify({
      accountId: route.params.accountId,
      nonce: Math.random().toString(36).slice(2),
    })
  );

  const params = new URLSearchParams({
    client_id: clientId.value,
    response_type: 'code',
    redirect_uri: redirectUri.value,
    scope: 'full_read_write offline',
    state,
  });

  window.location.href = `${OAUTH_BASE_URL}?${params.toString()}`;
};

const handleOAuthCallback = async () => {
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (!stored) {
    errorMessage.value = t('INTEGRATION_SETTINGS.AKILES.ERRORS.SESSION_EXPIRED');
    return;
  }

  const { clientId: storedClientId, clientSecret: storedClientSecret } = JSON.parse(stored);
  sessionStorage.removeItem(SESSION_KEY);

  isConnecting.value = true;
  try {
    await store.dispatch('integrations/connectAkiles', {
      code: props.code,
      clientId: storedClientId,
      clientSecret: storedClientSecret,
      redirectUri: redirectUri.value,
    });
    router.replace(route.path);
  } catch {
    errorMessage.value = t('INTEGRATION_SETTINGS.AKILES.ERRORS.CONNECTION_FAILED');
  } finally {
    isConnecting.value = false;
  }
};

watch(isConnected, connected => {
  if (connected) loadSettingsFromHook();
});

onMounted(async () => {
  await store.dispatch('integrations/get', 'akiles');

  if (props.code) {
    await handleOAuthCallback();
  }

  if (isConnected.value) loadSettingsFromHook();
  integrationLoaded.value = true;
});
</script>

<template>
  <SettingsLayout :is-loading="!integrationLoaded || isConnecting || uiFlags.isCreatingAkiles">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.AKILES.HEADER')"
        description=""
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <Integration
          :integration-id="integration.id"
          :integration-logo="integration.logo"
          :integration-name="integration.name"
          :integration-description="integration.description"
          :integration-enabled="isConnected"
          :integration-action="isConnected ? 'disconnect' : ''"
          :action-button-text="$t('INTEGRATION_SETTINGS.AKILES.DELETE')"
          :delete-confirmation-text="{
            title: $t('INTEGRATION_SETTINGS.AKILES.DELETE_CONFIRMATION.TITLE'),
            message: $t('INTEGRATION_SETTINGS.AKILES.DELETE_CONFIRMATION.MESSAGE'),
          }"
        >
          <template #action>
            <Button
              teal
              :label="$t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
              @click="openConnectForm"
            />
          </template>
        </Integration>

        <div
          v-if="isConnected"
          class="flex flex-col gap-4 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-6"
        >
          <p class="text-n-teal-9">
            {{ $t('INTEGRATION_SETTINGS.AKILES.CONNECTED_MESSAGE') }}
          </p>
          <div class="border-t border-n-container pt-4 mt-2">
            <h3 class="text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.TITLE') }}
            </h3>
            <p class="text-xs text-n-slate-11 mb-4">
              {{ $t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.DESCRIPTION') }}
            </p>
            <div class="flex flex-col gap-4">
              <Input
                v-model="whatsappInboxId"
                :label="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.WHATSAPP_INBOX_ID')"
                :placeholder="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.WHATSAPP_INBOX_ID_PLACEHOLDER')"
                :help-text="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.WHATSAPP_INBOX_ID_HELP')"
              />
              <Input
                v-model="bookingTemplateName"
                :label="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.BOOKING_TEMPLATE')"
                :placeholder="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.BOOKING_TEMPLATE_PLACEHOLDER')"
                :help-text="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.BOOKING_TEMPLATE_HELP')"
              />
              <Input
                v-model="checkinTemplateName"
                :label="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.CHECKIN_TEMPLATE')"
                :placeholder="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.CHECKIN_TEMPLATE_PLACEHOLDER')"
                :help-text="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.CHECKIN_TEMPLATE_HELP')"
              />
              <div class="flex justify-end">
                <Button
                  teal
                  :label="$t('INTEGRATION_SETTINGS.AKILES.SETTINGS_FORM.SAVE')"
                  :is-loading="uiFlags.isUpdatingAkiles"
                  @click="saveSettings"
                />
              </div>
            </div>
          </div>
        </div>

        <Dialog
          ref="dialogRef"
          :title="$t('INTEGRATION_SETTINGS.AKILES.CONNECT_FORM.TITLE')"
          :description="$t('INTEGRATION_SETTINGS.AKILES.CONNECT_FORM.DESCRIPTION')"
          :confirm-button-label="$t('INTEGRATION_SETTINGS.AKILES.CONNECT_FORM.SUBMIT')"
          :cancel-button-label="$t('INTEGRATION_SETTINGS.AKILES.CONNECT_FORM.CANCEL')"
          :is-loading="isConnecting"
          :disable-confirm-button="!clientId || !clientSecret"
          @confirm="startOAuthFlow"
          @close="cancelConnect"
        >
          <div class="flex flex-col gap-4 mt-2">
            <Input
              v-model="clientId"
              :label="$t('INTEGRATION_SETTINGS.AKILES.CONNECT_FORM.CLIENT_ID')"
              :placeholder="$t('INTEGRATION_SETTINGS.AKILES.CONNECT_FORM.CLIENT_ID_PLACEHOLDER')"
            />
            <Input
              v-model="clientSecret"
              type="password"
              :label="$t('INTEGRATION_SETTINGS.AKILES.CONNECT_FORM.CLIENT_SECRET')"
              :placeholder="$t('INTEGRATION_SETTINGS.AKILES.CONNECT_FORM.CLIENT_SECRET_PLACEHOLDER')"
            />
            <p v-if="errorMessage" class="text-n-ruby-9 text-sm">
              {{ errorMessage }}
            </p>
          </div>
        </Dialog>
      </div>
    </template>
  </SettingsLayout>
</template>
